import { isVatActive, isVatUndefined } from './vat';

export const cartDataSource = {
	LOCALE: 'locale',
	PRICING: 'pricing',
};

export const cartType = {
	ESRISTORE: 'esristore',
	BUILDANDSHARE: 'buildandshare',
};

export const priceType = {
	PRICE: 'price',
	QUANTITY_ONE_PRICE: 'quantityOnePrice',
};

const privatePriceType = {
	CORE_PRICE: 'corePrice',
};

/** @return {PricingTier} */
export function findPricingTierByQty(pricingTiers, qty) {
	if (!pricingTiers?.length) return null;

	return pricingTiers.find((pricingTier) => {
		if (pricingTier._id === 'mockPricingTier') return true;

		return qty >= pricingTier.qtyFrom && qty <= pricingTier.qtyTo;
	});
}

/** @return {number} */
export function getItemQty(item) {
	let totalQty = item.quantity;
	if (!item?.metadata?.subscriptions?.selected?.length) return totalQty;

	// For certain products discount eligbility needs to include current entitled quantity.
	item.metadata.subscriptions.selected.forEach((selectedSubscription) => {
		if (!selectedSubscription.usersEligibility?.[item.materialNumber]) return;

		totalQty += selectedSubscription.usersEligibility[item.materialNumber].currentEntitled;
	});

	// In some cases, the total quantity is current quantity selected
	// plus the quantity that already exists for that customer for that product.
	return totalQty;
}

/** @return {boolean} */
export function hasSubscriptionSelected(item) {
	return !!item.metadata?.subscriptions?.selected?.length;
}

export function isCoreLicenseSelector(item) {
	return item?.metadata?.subscriptions?.selectorType === 'coreLicenseSelector';
}

/** @return {boolean} */
export function hasInvalidItemPrice(cart) {
	if (!cart?.lineItems?.length || isVatUndefined()) return false;

	// Check if any of the cart item vat prices are invalid based on the current vat selection.
	return cart.lineItems.some((item) => {
		const prices = getItemPrices(item);
		const validPrice = prices.price;

		return !!validPrice && validPrice !== item.price;
	});
}

/**
 * Resolves the price type based on the item and the provided type.
 * @param {Object} item - The item.
 * @param {string} _type - The price type.
 * @return {string} */
export function resolvePriceType(item, type) {
	if (type === priceType.PRICE && isCoreLicenseSelector(item) && hasSubscriptionSelected(item)) {
		// Core license with active subscription, use core price
		type = privatePriceType.CORE_PRICE;
	} else if (type === priceType.PRICE && !hasSubscriptionSelected(item)) {
		// No active subscription, use single quantity price to exclude volume discounts
		type = priceType.QUANTITY_ONE_PRICE;
	}

	return type;
}

/**
 * Calculates the item price based on the provided type.
 * @param {Object} item - The item.
 * @param {string} type - The price type.
 * @return {number} */
export function calculateItemPrice(item, type) {
	const vatActive = isVatActive();
	const resolvedType = resolvePriceType(item, type);
	switch (resolvedType) {
		case priceType.PRICE:
		case priceType.QUANTITY_ONE_PRICE: {
			// Calculate price based on active subscription and assigned quantity, including pro-rated pricing and volume discounts.
			// If type is QUANTITY_ONE_PRICE, calculate price for a single quantity to exclude volume discounts.
			const max = item.metadata?.maxQtyAllowed || Number.MAX_SAFE_INTEGER;
			const quantity = type === priceType.QUANTITY_ONE_PRICE ? 1 : Math.min(getItemQty(item), max);
			const pricingTier = findPricingTierByQty(item?.metadata?.pricing?.pricingTiers, quantity);
			const price = vatActive ? pricingTier?.vatAmount : pricingTier?.amount;
			return price;
		}
		case privatePriceType.CORE_PRICE: {
			// Calculate the total price based on the quantities and prices of the selected subscriptions
			const price = item.metadata.subscriptions.selected.reduce((acc, subscription) => {
				if (subscription?.dates?.[0]?.pricingTiers) {
					const assignedQty = subscription.assignedQty;
					const pricingTier = findPricingTierByQty(subscription.dates[0].pricingTiers, assignedQty);
					const _price = vatActive ? pricingTier?.vatAmount : pricingTier?.amount;

					return acc + _price * assignedQty;
				}

				return acc;
			}, 0);
			return price / getItemQty(item);
		}
		default:
			return null;
	}
}

/**
 * Gets the item prices based on the current vat selection.
 * @param {Object} item - The item.
 * @return {prices} */
export function getItemPrices(item) {
	const price = (!isVatUndefined() && calculateItemPrice(item, priceType.PRICE)) || item.price;
	const quantityOnePrice = (!isVatUndefined() && calculateItemPrice(item, priceType.QUANTITY_ONE_PRICE)) || item.metadata?.pricing?.quantityOnePrice;

	return {
		price,
		quantityOnePrice,
	};
}

/** @return {void} */
export function recalculateCartPrices(cart) {
	if (!cart?.lineItems?.length || isVatUndefined()) return;

	return cart.lineItems.forEach((item) => {
		const prices = getItemPrices(item);
		item.price = prices.price;
		if (item.metadata?.pricing) {
			item.metadata.pricing.quantityOnePrice = prices.quantityOnePrice;
		}
	});
}

/**
 * Converts cart data to a cart model.
 * @param {Object} cartData - The cart data.
 * @param {string} [sourceType=cartDataSource.PRICING] - The source type of the cart data.
 * @returns {Object} The cart model.
 */
export function toCartModel(cartData, sourceType = cartDataSource.PRICING) {
	let model;
	const template = {
		channel: cartData.channel || 'eCommerce',
		cartType: cartData.cartType || cartType.ESRISTORE,
		lineItems: cartData.lineItems || [],
		status: cartData.status || 'new',
	};

	if (sourceType === cartDataSource.LOCALE) {
		model = {
			...template,
			locale: cartData.locale.supportedCurrencies[0].storeLocale,
			zone: cartData.locale.zone,
			currency: cartData.locale.supportedCurrencies[0].currencyCode,
			distributorNumber: cartData.locale.distributorNumber,
			metadata: {
				isOmni: cartData.locale.isOmni,
				storeDomain: cartData.locale.storeDomain,
				formattedLocale: cartData.locale.supportedLocales[0].formattedLocale,
				currency: cartData.locale.supportedCurrencies[0],
			},
			...(cartData.username && { username: cartData.username }),
		};
	} else if (sourceType === cartDataSource.PRICING) {
		model = {
			...template,
			locale: cartData.locale,
			zone: cartData.zone,
			currency: cartData.currency,
			distributorNumber: parseInt(cartData.distributorNumber),
			metadata: cartData.sessionMetadataUpdate || cartData.metadata,
			...(cartData.customerNumber && { customerNumber: cartData.customerNumber }),
			...(cartData.createDate && { createDate: cartData.createDate }),
		};
	}

	return model;
}

/**
 * Check if the item is personal or student use.
 * @param {Object} item - The item.
 * @return {void}
 */
export function isPersonalOrStudentUse(item) {
	const materialNumber = item?.materialNumber != null ? item.materialNumber.toString() : null;
	if (!materialNumber) return false;

	return materialNumber === '165619' || materialNumber === '165620';
}
